import { selectAcceptedDocuments } from '../../library/accepted-documents';
import { getPrimaryDocumentMessage } from '../../library/document-display';
import { getPrimaryDocumentStatus } from '../../library/document-status';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Slide, Snackbar, Toolbar, Typography, useTheme } from '@mui/material';

import TopNavBar from './TopNavBar/TopNavBar';
import BottomNavBar from './BottomNavBar/BottomNavBar';
import Router from '../../router';

import { resetAppNotification } from '../../store/actions/app-actions';
import { fetchAllBanners, setAnnouncementsModalVisible, setBannersModalVisible } from '../../store/actions/banner-actions';

import mainLayoutStyles from './mainLayoutStyles';
import ReferralCodeDisplay from '../../components/ReferralCode/ReferralCodeDisplay';
import config from '../../config';
import BannerModal from '../../components/banners/BannerModal';
import AnnouncementModal from '../../components/banners/AnnouncementModal';

const MainLayout = () => {
  const acceptedDocuments = useSelector(selectAcceptedDocuments);
  const styles = mainLayoutStyles();
  const theme = useTheme();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userPending = useSelector((state) => state.auth.userPending);
  const userProfilePending = useSelector((state) => state.auth.userProfilePending);
  const isPinRequired = useSelector((state) => state.app.isPinRequired);
  const isAccountPinSet = useSelector((state) => state.auth.isAccountPinSet);
  const isEmailConfirmed = useSelector((state) => state.auth.isEmailConfirmed);
  const isPhoneNumberConfirmed = useSelector((state) => state.auth.isPhoneNumberConfirmed);
  const kycPending = useSelector((state) => state.auth.kycPending);
  const kycExpiredIncoming = useSelector((state) => state.auth.kycExpiredIncoming);
  const kycQnAPending = useSelector((state) => state.auth.kycQnAPending);
  const kycQnAExpiredIncoming = useSelector((state) => state.auth.kycQnAExpiredIncoming);
  const nationalIdInvalid = useSelector((state) => state.auth.nationalIdInvalid);
  const forceLinkAuthProcessor = useSelector((state) => state.auth.forceLinkAuthProcessor);
  const transactionsInCompliancePending = useSelector((state) => state.app.transactionsInCompliancePending);
  const transactionsInComplianceHold = useSelector((state) => state.app.transactionsInComplianceHold);
  const complianceEDDRequested = useSelector((state) => state.app.complianceEDDRequested);
  const notificationOpen = useSelector((state) => state.app.notificationOpen);
  const notificationSeverity = useSelector((state) => state.app.notificationSeverity);
  const notificationMessage = useSelector((state) => state.app.notificationMessage);
  const isAccountContactsVerificationRequired = useSelector((state) => state.app.isAccountContactsVerificationRequired);
  const userCountry = useSelector((state) => state.auth.user.country);

  const documents = useSelector((state) => state.auth.user.document);
  const documentStatus = getPrimaryDocumentStatus(documents, acceptedDocuments);
  const documentMessage = getPrimaryDocumentMessage(documentStatus, acceptedDocuments);

  const handleCloseNotification = () => {
    dispatch(resetAppNotification());
  };

  // banner login
  useEffect(() => {
    if (isLoggedIn && config.REACT_APP_BANNER_ENABLED === 'true') {
      const successCallback = (bannerList, announcementList) => {
        if (bannerList.length > 0) {
          const isBannerAlreadyShown = localStorage.getItem('bannerShown');
          if (!isBannerAlreadyShown) {
            if (announcementList.length > 0) {
              dispatch(setAnnouncementsModalVisible(true));
            } else if (bannerList.length > 0) {
              dispatch(setBannersModalVisible(true));
            }
          }
        }
      };
      dispatch(fetchAllBanners(userCountry, successCallback));
    }
  }, [dispatch, isLoggedIn, userCountry]);

  return (
    <div className={styles.root}>
      <TopNavBar />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={notificationOpen}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseNotification} severity={notificationSeverity} sx={{ width: '100%' }}>
          {notificationMessage}
        </Alert>
      </Snackbar>
      {(userPending ||
        (isAccountContactsVerificationRequired && (!isEmailConfirmed || !isPhoneNumberConfirmed)) ||
        kycPending ||
        kycExpiredIncoming ||
        kycQnAPending ||
        kycQnAExpiredIncoming ||
        userProfilePending ||
        (isPinRequired && !isAccountPinSet) ||
        nationalIdInvalid ||
        forceLinkAuthProcessor ||
        transactionsInComplianceHold ||
        complianceEDDRequested) &&
        isLoggedIn && (
          <div
            style={{
              position: theme.config.isTopNavbarFixed ? 'fixed' : 'relative',
              width: '100%',
              zIndex: '2',
            }}
          >
            {theme.config.isTopNavbarFixed && <Toolbar sx={{ margin: '16px' }} />}
            <Alert severity="warning">
              {(userPending ||
                (isAccountContactsVerificationRequired && (!isEmailConfirmed || !isPhoneNumberConfirmed)) ||
                kycPending ||
                kycExpiredIncoming ||
                kycQnAPending ||
                kycQnAExpiredIncoming ||
                userProfilePending ||
                nationalIdInvalid ||
                forceLinkAuthProcessor) && (
                <Typography>
                  {kycExpiredIncoming
                    ? 'One of your KYC document is almost expiring.'
                    : kycQnAExpiredIncoming
                      ? 'Your KYC questionnaire is almost expiring.'
                      : 'Your account is in pending status.'}
                  {isAccountContactsVerificationRequired && (!isEmailConfirmed || !isPhoneNumberConfirmed) ? (
                    <>
                      {' '}
                      Please go to <Link to="/account/contacts">account/contacts</Link> to verify your contact details.
                    </>
                  ) : kycPending || kycExpiredIncoming ? (
                    <>
                      {' '}
                      {kycPending ? documentMessage : 'Your identity document is expiring soon.'} <Link to="/account/documents">Review documents</Link>.
                    </>
                  ) : kycQnAPending || kycQnAExpiredIncoming ? (
                    <>
                      {' '}
                      Please go to <Link to="/account/questionnaire">account/questionnaire</Link> to complete KYC form.
                    </>
                  ) : userProfilePending ? (
                    <>
                      {' '}
                      Please go to <Link to="/account/profile">account/profile</Link> to update your profile.
                    </>
                  ) : nationalIdInvalid ? (
                    <>
                      {' '}
                      Invalid National ID number. Please go to <Link to="/account/documents">account/documents</Link> and upload your KYC
                      document.
                    </>
                  ) : forceLinkAuthProcessor ? (
                    <>
                      {' '}
                      Identity provider not linked. Please go to <Link to="/account/settings">account/settings</Link> and link your account.
                    </>
                  ) : (
                    ' You will receive a notification once the admin approved.'
                  )}
                </Typography>
              )}
              {transactionsInCompliancePending && (
                <Typography>
                  You have a transaction requiring additional document/s. Please go to{' '}
                  <Link to="/account/transactions">account/transactions</Link> to upload the necessary document/s.
                </Typography>
              )}
              {isPinRequired && !isAccountPinSet && (
                <Typography>
                  Your PIN is not yet set. Please go to <Link to="/account/settings">account/settings</Link> to set your PIN.
                </Typography>
              )}
              {transactionsInComplianceHold && !!!transactionsInCompliancePending && (
                <Typography>You have a transaction pending compliance approval.</Typography>
              )}
              {complianceEDDRequested && (
                <Typography>
                  You are requested to provide additional compliance document/s. Please go to{' '}
                  <Link to="/account/compliance">account/compliance</Link> to upload the necessary document/s.
                </Typography>
              )}
            </Alert>
          </div>
        )}
      <Router />
      <BottomNavBar />
      <BannerModal />
      <AnnouncementModal />
      {config.REACT_APP_REFERRAL_CODE_ENABLED === 'true' && <ReferralCodeDisplay />}
    </div>
  );
};

export default MainLayout;
